/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * MANUALLY CHANGE A VARIANT
 * ------------------------------------------------------------------------------------------------------------
 *
 * You may want to manually change the variant, and let the theme automatically adjust all the selectors. To do
 * that, you can get the DOM element of type "<product-variants>", and call the selectVariant method on it with
 * the variant ID.
 *
 * Example:
 *
 * const productVariantElement = document.querySelector('product-variants');
 * productVariantElement.selectVariant(12345);
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */

/** 
 * ------------------------------------------------------------------------------------------------------------
 * React on cart changes
 * ------------------------------------------------------------------------------------------------------------
 */

 document.addEventListener('cart:updated', function(event) {
   window.dispatchEvent(new CustomEvent('cart.update', { detail: { cart: event.detail.cart } }));
 });

/**
 * ------------------------------------------------------------------------------------------------------------
 * VARIANT IMAGE LIST - Single gallery with manual rerender on variant change
 *
 * The theme's allow-partial-rerender doesn't include product-media in its block types.
 * We listen to product:rerender and manually replace the gallery, then reinitialize Flickity.
 * This enables the single-gallery optimization (~30KB instead of ~650KB per cached response).
 * ------------------------------------------------------------------------------------------------------------
 */

var VariantImageListHandler = {
  // Track pending swap to handle rapid clicks
  pendingSwap: null,
  // Track current color to only swap gallery on color change (not size change)
  currentColor: null,

  init: function() {
    // Find all product forms and attach listeners
    document.querySelectorAll('form[id^="product-form-"]').forEach(form => {
      form.addEventListener('product:rerender', this.onProductRerender.bind(this));
    });

    // Initialize currentColor from existing gallery
    const existingMedia = document.querySelector('product-media[data-variant-image-list]');
    if (existingMedia && existingMedia.dataset.selectedColor) {
      this.currentColor = existingMedia.dataset.selectedColor;
    }
  },

  onProductRerender: function(event) {
    const startTime = performance.now();
    const htmlFragment = event.detail.htmlFragment;
    if (!htmlFragment) return;

    // Find the new product-media in the fragment
    const newProductMedia = htmlFragment.querySelector('product-media[data-variant-image-list]');
    if (!newProductMedia) return;

    // Check if color changed - if not, skip the swap (user stays on current image)
    const newColor = newProductMedia.dataset.selectedColor;
    if (this.currentColor && newColor && this.currentColor === newColor) {
      console.log('[VariantImageList] Same color (' + newColor + '), skipping gallery swap');
      return;
    }

    // Update current color
    this.currentColor = newColor;

    // Cancel any pending swap
    if (this.pendingSwap) {
      this.pendingSwap.cancelled = true;
    }

    // Find ALL existing product-media elements with data-variant-image-list
    const existingMediaElements = document.querySelectorAll('product-media[data-variant-image-list]');
    if (existingMediaElements.length === 0) return;

    // Get the parent container and the next sibling for correct insertion position
    const referenceElement = existingMediaElements[0];
    const parent = referenceElement.parentNode;
    const nextSibling = referenceElement.nextElementSibling;

    // IMPORTANT: Remove old elements FIRST before inserting new one
    // This prevents duplicate ID issues (flickity-carousel and flickity-controls
    // use getElementById to find each other)
    existingMediaElements.forEach(el => {
      el.remove();
    });

    // Insert new element at the same position with opacity:0, then fade in
    newProductMedia.style.opacity = '0';
    newProductMedia.style.transition = 'opacity 0.15s ease-out';

    if (nextSibling) {
      parent.insertBefore(newProductMedia, nextSibling);
    } else {
      parent.appendChild(newProductMedia);
    }

    // Create swap context for cancellation tracking
    const swapContext = {
      cancelled: false,
      completed: false
    };
    this.pendingSwap = swapContext;

    // Function to show the new element
    const showNewElement = () => {
      if (swapContext.cancelled || swapContext.completed) return;
      swapContext.completed = true;

      newProductMedia.style.opacity = '';
      setTimeout(() => {
        newProductMedia.style.transition = '';
      }, 150);

      if (this.pendingSwap === swapContext) {
        this.pendingSwap = null;
      }

      console.log('[VariantImageList] Swap completed in', (performance.now() - startTime).toFixed(1), 'ms');
    };

    // Wait for Flickity to be ready
    const newCarousel = newProductMedia.querySelector('flickity-carousel');
    if (newCarousel) {
      let pollCount = 0;
      const maxPolls = 50;

      const checkFlickity = () => {
        if (swapContext.cancelled) return;
        pollCount++;

        if (newCarousel.flickityInstance) {
          newCarousel.flickityInstance.then(() => {
            requestAnimationFrame(() => {
              requestAnimationFrame(() => {
                requestAnimationFrame(showNewElement);
              });
            });
          }).catch(() => {
            requestAnimationFrame(showNewElement);
          });
        } else if (pollCount < maxPolls) {
          requestAnimationFrame(checkFlickity);
        } else {
          requestAnimationFrame(showNewElement);
        }
      };
      requestAnimationFrame(checkFlickity);
    } else {
      // No Flickity carousel - show immediately
      requestAnimationFrame(showNewElement);
    }
  }
};

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', function() {
    VariantImageListHandler.init();
  });
} else {
  VariantImageListHandler.init();
}

/**
 * ------------------------------------------------------------------------------------------------------------
 * EXTERNAL URL LIGHTBOX - Simple fullscreen zoom for variant_image_list images
 *
 * Since PhotoSwipe requires Shopify media IDs, we use a simple lightbox for external URLs.
 * Reads image dimensions from naturalWidth/naturalHeight of the already-loaded image.
 * ------------------------------------------------------------------------------------------------------------
 */

var ExternalUrlLightbox = {
  overlay: null,
  currentIndex: 0,
  images: [],

  init: function() {
    this.createOverlay();
    this.bindEvents();
  },

  createOverlay: function() {
    if (document.getElementById('external-url-lightbox')) return;

    var overlay = document.createElement('div');
    overlay.id = 'external-url-lightbox';
    overlay.className = 'external-url-lightbox';
    overlay.innerHTML = `
      <div class="external-url-lightbox__backdrop"></div>
      <div class="external-url-lightbox__content">
        <img class="external-url-lightbox__image" src="" alt="">
      </div>
      <button class="external-url-lightbox__close" aria-label="Close">
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <line x1="18" y1="6" x2="6" y2="18"></line>
          <line x1="6" y1="6" x2="18" y2="18"></line>
        </svg>
      </button>
      <button class="external-url-lightbox__prev" aria-label="Previous">
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <polyline points="15 18 9 12 15 6"></polyline>
        </svg>
      </button>
      <button class="external-url-lightbox__next" aria-label="Next">
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <polyline points="9 18 15 12 9 6"></polyline>
        </svg>
      </button>
    `;
    document.body.appendChild(overlay);
    this.overlay = overlay;
  },

  bindEvents: function() {
    var self = this;

    // Open lightbox when clicking zoom button for external URLs
    document.addEventListener('click', function(e) {
      var zoomBtn = e.target.closest('.product__zoom-button--external');
      if (zoomBtn) {
        e.preventDefault();
        // Try to find the gallery container - could be .product__media-gallery (multi-gallery mode) or .product__media (single gallery)
        var gallery = zoomBtn.closest('.product__media-gallery') || zoomBtn.closest('.product__media');
        if (gallery) {
          self.openFromGallery(gallery);
        }
      }
    });

    // Close button
    this.overlay.querySelector('.external-url-lightbox__close').addEventListener('click', function() {
      self.close();
    });

    // Backdrop click to close
    this.overlay.querySelector('.external-url-lightbox__backdrop').addEventListener('click', function() {
      self.close();
    });

    // Navigation
    this.overlay.querySelector('.external-url-lightbox__prev').addEventListener('click', function() {
      self.prev();
    });

    this.overlay.querySelector('.external-url-lightbox__next').addEventListener('click', function() {
      self.next();
    });

    // Keyboard navigation
    document.addEventListener('keydown', function(e) {
      if (!self.overlay.classList.contains('is-open')) return;

      if (e.key === 'Escape') {
        self.close();
      } else if (e.key === 'ArrowLeft') {
        self.prev();
      } else if (e.key === 'ArrowRight') {
        self.next();
      }
    });
  },

  openFromGallery: function(gallery) {
    var self = this;
    var mediaItems = gallery.querySelectorAll('.product__media-item');
    var selectedItem = gallery.querySelector('.product__media-item.is-selected');

    this.images = [];
    this.currentIndex = 0;

    mediaItems.forEach(function(item, index) {
      var img = item.querySelector('img');
      if (img) {
        self.images.push({
          src: img.src,
          alt: img.alt || ''
        });
        if (item === selectedItem) {
          self.currentIndex = index;
        }
      }
    });

    if (this.images.length > 0) {
      this.open();
    }
  },

  open: function() {
    this.showImage(this.currentIndex);
    this.overlay.classList.add('is-open');
    document.body.style.overflow = 'hidden';
    this.updateNavButtons();
  },

  close: function() {
    this.overlay.classList.remove('is-open');
    document.body.style.overflow = '';
  },

  showImage: function(index) {
    var img = this.overlay.querySelector('.external-url-lightbox__image');
    img.src = this.images[index].src;
    img.alt = this.images[index].alt;
  },

  prev: function() {
    if (this.currentIndex > 0) {
      this.currentIndex--;
      this.showImage(this.currentIndex);
      this.updateNavButtons();
    }
  },

  next: function() {
    if (this.currentIndex < this.images.length - 1) {
      this.currentIndex++;
      this.showImage(this.currentIndex);
      this.updateNavButtons();
    }
  },

  updateNavButtons: function() {
    var prevBtn = this.overlay.querySelector('.external-url-lightbox__prev');
    var nextBtn = this.overlay.querySelector('.external-url-lightbox__next');

    prevBtn.style.display = this.currentIndex > 0 ? '' : 'none';
    nextBtn.style.display = this.currentIndex < this.images.length - 1 ? '' : 'none';

    // Hide nav buttons if only one image
    if (this.images.length <= 1) {
      prevBtn.style.display = 'none';
      nextBtn.style.display = 'none';
    }
  }
};

// Initialize lightbox when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', function() {
    ExternalUrlLightbox.init();
  });
} else {
  ExternalUrlLightbox.init();
}


/**
 * ------------------------------------------------------------------------------------------------------------
 * DATORACODE  Auto Discount
 *
 * Sets a discount code from shop metafield if the customer has none.
 * Integrates with datora.js.liquid state (sessionStorage.lastDiscountCode).
 * ------------------------------------------------------------------------------------------------------------
 */
(function() {
  var cfg = window.cfAutoDiscount;
  if (!cfg || !cfg.enabled) return;
  var code = (cfg.code || '').trim();
  if (!code) return;

  var applied = false;

  async function tryApply() {
    if (applied) return;
    if (typeof co2 === 'undefined' || typeof co2.getDiscountCode !== 'function' || typeof co2.setDiscountCode !== 'function') return;
    applied = true;
    try {
      var currentCode = await co2.getDiscountCode();
      currentCode = (currentCode || '').trim();
      if (currentCode) return;
      var success = await co2.setDiscountCode(code);
      if (success) {
        sessionStorage.setItem('lastDiscountCode', code.toUpperCase());
        document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
      }
    } catch (e) {
      applied = false;
    }
  }

  window.addEventListener('co2.cart.initialized', tryApply);
})();


/**
 * ------------------------------------------------------------------------------------------------------------
 * DATORACODE  Upsell Modal im Cart-Drawer
 *
 * Lädt die eigene Section "upsell-product-form" via ?section_id.
 * Die Section enthält keine App-Blöcke → kein Sizekick-Konflikt.
 * Re-Renders (Variantenwechsel) nutzen denselben section_id-Mechanismus.
 * ------------------------------------------------------------------------------------------------------------
 */
(function() {
  function initUpsellModal() {
    var modal = document.getElementById('upsell-modal');
    if (!modal) return;

    // Datora Discount gezielt für das Upsell-Modal berechnen
    function applyUpsellDiscount(container) {
      var el = container.querySelector('[data-datora-pid]');
      if (!el || typeof co2 === 'undefined' || typeof co2.itemDiscountFull !== 'function') return;
      co2.getDiscountCode().then(function(code) {
        if (!code) return;
        var pid = Number(el.dataset.datoraPid);
        var vid = Number(el.dataset.datoraVid);
        var tags = el.dataset.datoraTags ? el.dataset.datoraTags.split(',') : [];
        var price = Number(el.dataset.datoraPrice);
        var spgid = el.dataset.datoraSpgid || null;
        co2.itemDiscountFull(pid, vid, tags, price, 1, spgid).then(function(data) {
          if (!data) return;
          var validDisc = false;
          var newPrice, badgeText;
          if (data.calc === 'perc' && data.discPerc > 0) {
            validDisc = true;
            newPrice = formatPrice(data.discPrice);
            badgeText = '<b>-' + data.discPerc + '%</b> (' + code.toUpperCase() + ')';
          } else if (data.calc === 'amt' && data.discAmt > 0) {
            validDisc = true;
            var discounted = data.discPrice;
            newPrice = formatPrice(discounted);
            badgeText = '-' + formatPrice(price - discounted) + ' | ' + code.toUpperCase();
          }
          if (validDisc) {
            el.classList.add('discounted');
            var priceContainer = el.querySelector('.price-list .price:not(.price--compare)');
            setPriceText(priceContainer, newPrice);
            setDatoraClasses(el, formatPrice(price));
            el.querySelector('[data-datora]').innerHTML = badgeText;
            var badge = el.querySelector('.datora.discount-badge');
            if (badge) badge.removeAttribute('hidden');
          }
        });
      });
    }

    // Referenz auf den Cart Drawer, um dessen Focus-Trap zu pausieren/fortsetzen
    var drawer = document.getElementById('mini-cart');

    function pauseDrawerTrap() {
      if (drawer && drawer.focusTrap) {
        try { drawer.focusTrap.pause(); } catch(e) {}
      }
    }
    function unpauseDrawerTrap() {
      if (drawer && drawer.focusTrap) {
        try { drawer.focusTrap.unpause(); } catch(e) {}
      }
    }

    function openModal() {
      pauseDrawerTrap();
      modal.hidden = false;
    }
    function closeModal() {
      modal.hidden = true;
      unpauseDrawerTrap();
    }

    // Overlay-Klick oder Close-Button schließt Modal
    modal.addEventListener('click', function(e) {
      if (e.target.classList.contains('upsell-modal__overlay') || e.target.closest('[data-upsell-close]')) {
        closeModal();
      }
    });

    var isGift = false;

    document.addEventListener('click', function(e) {
      var trigger = e.target.closest('[data-upsell-trigger]');
      if (!trigger) return;
      var content = modal.querySelector('.upsell-modal__content');
      var href = trigger.getAttribute('data-upsell-href');
      isGift = trigger.getAttribute('data-upsell-gift') === 'true';

      if (content.children.length > 0 && href === currentHref && !isGift) {
        openModal();
        return;
      }

      loadProduct(content, href);
    });
    var currentHref = null;

    function loadProduct(content, href) {
      currentHref = href;
      var loadIsGift = isGift; // lokal sichern, damit async-Callbacks den richtigen Wert haben
      content.innerHTML = '<div class="upsell-modal__spinner"><span class="spinner">' +
        '<svg width="24" height="24" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2" fill="none" opacity="0.3"/>' +
        '<path d="M12 2a10 10 0 0 1 10 10" stroke="currentColor" stroke-width="2" fill="none" stroke-linecap="round"><animateTransform attributeName="transform" type="rotate" from="0 12 12" to="360 12 12" dur="1s" repeatCount="indefinite"/></path></svg>' +
        '</span></div>';
      openModal();

      var separator = href.includes('?') ? '&' : '?';
      fetch(href + separator + 'section_id=upsell-product-form')
        .then(function(r) { return r.text(); })
        .then(function(html) {
          // Bei Gift: ProductForm Custom Element deaktivieren BEVOR HTML ins DOM kommt
          if (loadIsGift) {
            html = html.replace(/\s+is="[^"]*"/g, '');
          }
          content.innerHTML = html;
          applyUpsellDiscount(content);

          // Bei Gift: Click auf Content-Ebene abfangen (Capture-Phase)
          if (loadIsGift) {
            var form = content.querySelector('form');
            if (form) {
              // Form-Submit komplett blockieren
              form.addEventListener('submit', function(ev) {
                ev.preventDefault();
                ev.stopImmediatePropagation();
              }, true);
            }

            // Click-Handler auf Content-Container (Capture-Phase, fängt alles ab)
            content.addEventListener('click', function giftClickHandler(ev) {
              var btn = ev.target.closest('button[type="submit"], [data-product-add-to-cart-button]');
              if (!btn) return;

              ev.preventDefault();
              ev.stopImmediatePropagation();

              var variantInput = content.querySelector('input[name="id"]');
              if (!variantInput) {
                return;
              }
              btn.setAttribute('disabled', 'disabled');

              fetch(window.Shopify.routes.root + 'cart/add.js', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  items: [{
                    id: parseInt(variantInput.value),
                    quantity: 1,
                    properties: { '_gift': '' }
                  }]
                })
              })
              .then(function() {
                btn.removeAttribute('disabled');
                closeModal();
                content.removeEventListener('click', giftClickHandler, true);
                // Section sofort ausblenden — verhindert kurzes Aufblitzen von "Geschafft"
                document.documentElement.dispatchEvent(new CustomEvent('gift:added'));
                document.documentElement.dispatchEvent(new CustomEvent('cart:refresh'));
              })
              .catch(function() {
                btn.removeAttribute('disabled');
                closeModal();
                content.removeEventListener('click', giftClickHandler, true);
              });
            }, true);
          }

          // Bei Variantenwechsel: Discount neu berechnen
          content.addEventListener('variant:changed', function() {
            applyUpsellDiscount(content);
          });

          // Nach Add-to-Cart: Modal schließen (nur bei nicht-Gift)
          if (!loadIsGift) {
            content.addEventListener('variant:added', function() {
              closeModal();
            });
          }
        });
    }

  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initUpsellModal);
  } else {
    initUpsellModal();
  }
})();

/**
 * ------------------------------------------------------------------------------------------------------------
 * DATORA-CODE: Gratisprodukt im Cart-Drawer
 * ------------------------------------------------------------------------------------------------------------
 */
(function() {
  var removingGift = false;
  var lastState = null; // Letzter bekannter Zustand (von co2.cart.initialized)
  var lastVisibleState = null; // Letzter sichtbarer Zustand (reached/pending) — für sofortiges Wiederanzeigen
  var recalcAfterRemove = false; // Nach Auto-Remove: beim nächsten cart:rerendered neu berechnen
  var giftWasInCart = false; // Geschenk war im Warenkorb (für Erkennung manueller Entfernung)

  // Section mit Inhalt befüllen (ohne Sichtbarkeit zu ändern)
  function fillSection(section, state) {
    var textEl = section.querySelector('[data-free-product-text]');
    var imgEl = section.querySelector('[data-free-product-img]');
    var titleEl = section.querySelector('[data-free-product-title]');
    var btnEl = section.querySelector('[data-free-product-btn]');

    imgEl.src = state.image;
    imgEl.alt = state.title;
    titleEl.textContent = state.title;
    textEl.innerHTML = state.text;
    btnEl.hidden = state.btnHidden;
    if (!state.btnHidden) {
      btnEl.setAttribute('data-upsell-href', state.btnHref);
    }
    section.classList.toggle('free-product-section--reached', state.reached);
    section.classList.toggle('free-product-section--pending', !state.reached);
  }

  // Section animiert einblenden (Höhe 0 → tatsächliche Höhe)
  function slideIn(section) {
    section.hidden = false;
    section.classList.add('free-product-section--collapsing');
    section.style.maxHeight = '0';
    // Force reflow damit der Browser den collapsed-State registriert
    section.offsetHeight;
    section.classList.remove('free-product-section--collapsing');
    section.style.maxHeight = section.scrollHeight + 'px';
    // Nach der Animation max-height entfernen
    section.addEventListener('transitionend', function handler() {
      section.style.maxHeight = '';
      section.removeEventListener('transitionend', handler);
    });
  }

  // Section animiert ausblenden (tatsächliche Höhe → 0, dann hidden)
  function slideOut(section, callback) {
    section.style.maxHeight = section.scrollHeight + 'px';
    section.offsetHeight;
    section.classList.add('free-product-section--collapsing');
    section.style.maxHeight = '0';
    section.addEventListener('transitionend', function handler() {
      section.classList.remove('free-product-section--collapsing');
      section.style.maxHeight = '';
      section.hidden = true;
      section.removeEventListener('transitionend', handler);
      if (callback) callback();
    });
  }

  // Zustand auf das aktuelle DOM anwenden (ohne co2 abzufragen)
  function applyState(animate) {
    if (!lastState) return;

    var section = document.getElementById('free-product-section');
    if (!section) return;

    if (lastState.hidden) {
      section.hidden = true;
      return;
    }

    fillSection(section, lastState);

    if (animate) {
      slideIn(section);
    } else {
      section.hidden = false;
    }
  }

  // co2-Daten abfragen und Zustand berechnen
  async function updateFreeProduct() {
    // console.log('[FREE-PRODUCT] updateFreeProduct called, removingGift=', removingGift);
    if (removingGift || typeof co2 === 'undefined') return;

    try {
      var unredeemed = await co2.unredeemedFreeProducts();
      var next = await co2.nextFreeProducts();

      // Gift-Handles im Warenkorb ermitteln (co2 erkennt eingelöste Gifts nicht zuverlässig)
      var giftHandlesInCart = [];
      var giftLinks = document.querySelectorAll('.line-item__gift-badge');
      giftLinks.forEach(function(badge) {
        var lineItem = badge.closest('.line-item');
        if (lineItem) {
          var link = lineItem.querySelector('a[href*="/products/"]');
          if (link) {
            var match = link.getAttribute('href').match(/\/products\/([^?]+)/);
            if (match) giftHandlesInCart.push(match[1]);
          }
        }
      });

      // Bereits eingelöste Gifts aus unredeemed filtern
      if (giftHandlesInCart.length > 0) {
        unredeemed = unredeemed.filter(function(item) {
          var handle = item.productItems[0];
          return giftHandlesInCart.indexOf(handle) === -1;
        });
      }

      // console.log('[FREE-PRODUCT] unredeemed=', unredeemed, 'next=', next, 'giftHandlesInCart=', giftHandlesInCart);

      if (!next && unredeemed.length === 0) {
        lastState = { hidden: true };
        lastVisibleState = null; // Kein Rabattcode mehr → nicht wiederherstellen
        applyState();
        return;
      }

      // Schritt 1: Aktuelles Gratisprodukt ermitteln
      var currentItem = null;
      var reached = false;

      if (unredeemed.length > 0) {
        currentItem = unredeemed[0];
        reached = true;
      } else if (next) {
        currentItem = next.items[0];
        reached = false;
      }

      if (!currentItem) {
        lastState = { hidden: true };
        lastVisibleState = null;
        applyState();
        return;
      }

      // Schritt 2: Produktdaten zum aktuellen Item finden
      var products = await co2.freeProductData();
      var handle = currentItem.productItems[0];
      var product = products.find(function(p) { return p.handle === handle; });
      // console.log('[FREE-PRODUCT] handle=', handle, 'product=', product, 'products=', products);

      if (!product) {
        lastState = { hidden: true };
        lastVisibleState = null;
        applyState();
        return;
      }

      // Schritt 3: Anzeige wie bisher
      if (reached) {
        lastState = {
          hidden: false,
          reached: true,
          image: product.image,
          title: product.title,
          text: document.getElementById('free-product-section').dataset.textReached,
          btnHidden: false,
          btnHref: window.Shopify.routes.root + 'products/' + product.handle
        };
      } else if (next) {
        var diff = next.threshold - next.sub_total;
        lastState = {
          hidden: false,
          reached: false,
          image: product.image,
          title: product.title,
          text: document.getElementById('free-product-section').dataset.textRemaining.replace('__AMOUNT__', formatMoney(diff)),
          btnHidden: true,
          btnHref: ''
        };
      }

      if (lastState && !lastState.hidden) {
        lastVisibleState = lastState;
      }
      applyState();
    } catch (e) {
      // co2 not ready yet, will retry on event
    }
  }

  // gift:added: Merken, dass Geschenk eingelöst wurde
  document.documentElement.addEventListener('gift:added', function() {
    giftWasInCart = true;
  });

  // co2.cart.initialized: co2 Daten abfragen und Zustand berechnen
  window.addEventListener('co2.cart.initialized', async function() {
    // console.log('[FREE-PRODUCT] co2.cart.initialized fired');
    var section = document.getElementById('free-product-section');
    var wasVisible = section && !section.hidden;
    // console.log('[FREE-PRODUCT] co2.cart.initialized: wasVisible=', wasVisible, 'lastState=', lastState);
    await updateFreeProduct();
    // console.log('[FREE-PRODUCT] co2.cart.initialized: after update, lastState=', lastState);
  });

  // cart:rerendered: co2-Daten neu abfragen (Rabattcode könnte entfernt worden sein)
  document.documentElement.addEventListener('cart:rerendered', async function() {
    // console.log('[FREE-PRODUCT] cart:rerendered fired');
    if (recalcAfterRemove) {
      // console.log('[FREE-PRODUCT] recalcAfterRemove path');
      recalcAfterRemove = false;
      updateFreeProduct();
      return;
    }

    // Geschenk manuell entfernt? → co2-Daten neu abfragen
    // console.log('[FREE-PRODUCT] giftWasInCart=', giftWasInCart, 'lastVisibleState=', lastVisibleState);
    if (giftWasInCart) {
      giftWasInCart = false;
      await updateFreeProduct();
      var section2 = document.getElementById('free-product-section');
      if (lastState && !lastState.hidden && section2 && section2.hidden) {
        applyState(true);
      }
      return;
    }

    // Vorherigen Zustand merken, dann co2-Daten neu abfragen
    var section = document.getElementById('free-product-section');
    var wasVisible = section && !section.hidden;
    // console.log('[FREE-PRODUCT] cart:rerendered: wasVisible=', wasVisible);
    await updateFreeProduct();
    // console.log('[FREE-PRODUCT] cart:rerendered: after update, lastState=', lastState, 'section.hidden=', section?.hidden);

    // Wenn sichtbar → hidden gewechselt: animiert ausblenden statt abrupt
    if (wasVisible && lastState && lastState.hidden && section) {
      section.hidden = false;
      slideOut(section);
    }
  });

  function initGiftAutoRemove() {
    document.documentElement.addEventListener('cart:rerendered', function() {
      var giftToRemove = document.querySelector('[data-gift-remove-key]');
      if (!giftToRemove) return;

      removingGift = true;
      fetch(window.Shopify.routes.root + 'cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: giftToRemove.dataset.giftRemoveKey,
          quantity: 0
        })
      })
      .then(function() {
        removingGift = false;
        recalcAfterRemove = true;
        document.documentElement.dispatchEvent(new CustomEvent('cart:refresh'));
      })
      .catch(function() {
        removingGift = false;
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function() {
      initGiftAutoRemove();
    });
  } else {
    initGiftAutoRemove();
  }
})();

 /* FIX: Re-render variant picker when no ?variant= URL parameter is present
 *
 * Shopify's value.variant returns the FIRST variant for each option value when no variant is
 * selected via URL. This causes hidden_on_storefront_variant_option checks to use the wrong
 * variant context, potentially hiding all size options. By triggering selectCombination on
 * page load, we force a server fetch with ?option_values= which provides proper context.
 * ------------------------------------------------------------------------------------------------------------
 */
(function() {
  function fixVariantPickerWithoutUrlParam() {
    console.log("%c-- DATORA Variant Checker --", "background: yellow; color: black; font-weight: bold; padding: 2px 5px;");
    
    if (new URLSearchParams(window.location.search).has('variant')) return;

    var picker = document.querySelector('variant-picker');
    if (!picker || !picker.selectedVariant || !picker.selectedVariant.id) return;

    var formId = picker.getAttribute('form-id');
    var form = document.forms[formId];
    if (!form) return;

    var selectedInputs = Array.from(form.elements)
      .filter(function(item) {
        return item.matches('input[data-option-position]:checked, input[data-option-position][type="hidden"]');
      })
      .sort(function(a, b) {
        return parseInt(a.getAttribute('data-option-position')) - parseInt(b.getAttribute('data-option-position'));
      });

    var corrected = false; // ******** Neues Flag > trackt ob eine Korrektur stattfand ********

    var optionValues = selectedInputs.map(function(input) {
      // Wenn die selektierte Option hidden-on-storefront ist, nimm erste sichtbare
      if (input.closest('.hidden-on-storefront')) {
        var position = input.getAttribute('data-option-position');
        var firstVisible = Array.from(form.elements).find(function(myel) {
          return myel.matches('input[data-option-position="' + position + '"]') &&
                 !myel.closest('.hidden-on-storefront');
        });
        if (firstVisible) {
          corrected = true; // ******** Wird gesetzt wenn ein hidden-Wert durch einen sichtbaren ersetzt wird ********

          console.log(
            "%c-------------- firstVisible -> corrected = true =>%c firstVisible.value = " + firstVisible.value + " / input.value = " + input.value,
            "background: lightblue; color: black; font-weight: bold; padding: 2px;",
            "background: transparent; color: inherit;"
          );

          return firstVisible.value;
        }
      }
      return input.value;
    });

    if (corrected && optionValues.length > 0) { // ******** selectCombination wird nur noch aufgerufen wenn corrected = true ********
      console.log("-------------- %ccorrected%c && optionValues.length > 0", "background: #ADD8E6; color: black; padding: 0 4px; border-radius: 3px;", "background: transparent;");
      picker.selectCombination({ optionValues: optionValues });
    }
  }

  if (document.readyState === 'loading') {
    console.log("-------------- document.readyState === 'loading'");
    document.addEventListener('DOMContentLoaded', fixVariantPickerWithoutUrlParam);
  } else {
    console.log("-------------- else -> fixVariantPickerWithoutUrlParam()");
    fixVariantPickerWithoutUrlParam();
  }
})();
